import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Loader2, Plug, CheckCircle2, ShieldCheck, RefreshCw, Unplug, ArrowRight, ExternalLink, Facebook, Instagram,
} from "lucide-react";

const FLOW = ["Login with Meta", "Select Business Portfolio", "Select Ad Account", "Select Page / Instagram", "Select Pixel / Dataset", "Authorize"];

export default function MetaAds() {
  const navigate = useNavigate();
  const [conn, setConn] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/meta/status").then((r) => setConn(r.data)).catch(() => setConn({ connected: false }));
  useEffect(() => { load(); }, []);

  const act = async (path, msg) => {
    setBusy(true);
    try { const r = await api.post(`/meta/${path}`); setConn(r.data.connected === false ? { connected: false } : r.data); toast.success(msg); }
    catch (e) { toast.error("Action failed"); }
    finally { setBusy(false); }
  };

  if (!conn) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (!conn.connected) {
    return (
      <div className="max-w-2xl mx-auto animate-float-up" data-testid="meta-hub-panel">
        <div className="bg-card border border-border rounded-2xl p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#1877F2]/15 flex items-center justify-center mx-auto mb-4"><Plug className="w-7 h-7 text-[#1877F2]" /></div>
          <h2 className="font-display text-2xl font-bold mb-2">Connect Meta Ads</h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">Connect once to power OTOSUBS Ads AI — analytics, campaign management (with approval) and revenue attribution. No manual developer/API setup.</p>
          <div className="flex flex-col gap-2 mb-8 text-left max-w-sm mx-auto">
            {FLOW.map((s, i) => (
              <div key={s} className="flex items-center gap-3 text-sm">
                <span className="w-6 h-6 rounded-full bg-secondary text-muted-foreground text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="text-muted-foreground">{s}</span>
              </div>
            ))}
          </div>
          <Button data-testid="meta-connect-btn" onClick={() => act("connect", "Meta Ads connected")} disabled={busy} className="bg-[#1877F2] hover:bg-[#1465d6] text-white font-semibold rounded-xl px-8">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plug className="w-4 h-4 mr-1" /> Connect with Meta</>}
          </Button>
          <p className="text-xs text-muted-foreground mt-4">Demo/simulated authorization for preview.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-float-up" data-testid="meta-hub-connected">
      <div className="bg-card border border-accent/30 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-accent/15 flex items-center justify-center"><CheckCircle2 className="w-6 h-6 text-accent" /></div>
          <div>
            <p className="font-display font-semibold">Meta Ads Connected</p>
            <p className="text-xs text-muted-foreground font-mono">Token valid until {new Date(conn.token_expiry).toLocaleDateString()} · synced {new Date(conn.last_sync_at).toLocaleTimeString()}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="meta-reconnect-btn" variant="outline" onClick={() => act("reconnect", "Reconnected & refreshed")} disabled={busy} className="rounded-xl"><RefreshCw className="w-4 h-4 mr-1" /> Reconnect</Button>
          <Button data-testid="meta-disconnect-btn" variant="outline" onClick={() => act("disconnect", "Disconnected")} disabled={busy} className="rounded-xl text-destructive hover:text-destructive"><Unplug className="w-4 h-4 mr-1" /> Disconnect</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {conn.asset_health.map((a) => (
          <div key={a.name} data-testid={`meta-asset-${a.name.toLowerCase().replace(/\s|\//g, "-")}`} className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{a.name}</span>
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-accent"><span className="w-2 h-2 rounded-full bg-accent" />{a.status}</span>
            </div>
            <p className="text-sm font-medium mt-1 truncate flex items-center gap-1">
              {a.name.includes("Facebook") && <Facebook className="w-3.5 h-3.5 text-[#1877F2]" />}
              {a.name.includes("Instagram") && <Instagram className="w-3.5 h-3.5 text-[#E4405F]" />}
              {a.value}
            </p>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-2xl p-5">
        <p className="text-xs font-mono uppercase tracking-widest text-primary mb-3 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Granted Permissions</p>
        <div className="flex flex-wrap gap-2">
          {conn.permissions.map((p) => (
            <span key={p.key} className="text-xs font-mono px-3 py-1.5 rounded-full bg-accent/10 text-accent border border-accent/20">{p.key} · {p.status}</span>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-4">This single connection powers OTOSUBS Ads AI, Optimization and Attribution — no five separate setups.</p>
      </div>

      <button data-testid="open-meta-review" onClick={() => navigate("/admin/meta-ads-review")} className="text-sm text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1">
        Open Meta Ads Review Center <ExternalLink className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
